import { useCallback, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { buscarPlaceDetailsSimples, buscarPlacesSimples, requestService } from '../Utils/Services';

/**
 * Interface para sugestão de endereço
 */
export interface SugestaoEndereco {
    placeId: string;
    descricao: string;
    descricaoPrincipal: string;
    descricaoSecundaria: string;
    tipos: string[];
}

/**
 * Interface para detalhes do endereço (versão simples - sem validação de entrega)
 */
export interface DetalhesEnderecoSimples {
    sucesso: boolean;
    endereco: string;
    nome: string;
    latitude: number;
    longitude: number;
    componentes: Array<{
        long_name: string;
        short_name: string;
        types: string[];
    }>;
    distancia?: {
        metros: number;
        km: number;
        descricao: string;
    };
}

/**
 * Props do hook
 */
interface UseGooglePlacesSimpleProps {
    onError?: (mensagem: string) => void;
    onLoading?: (loading: boolean) => void;
    debounceTime?: number; // Tempo de espera antes de buscar (padrão: 2000ms)
    minChars?: number; // Mínimo de caracteres para buscar (padrão: 4)
}

/**
 * Custom Hook para busca de endereços usando Google Places API (versão simplificada)
 * 
 * Este hook não requer configurações de empresa e não valida área de entrega.
 * Use para buscar apenas informações de localização.
 * 
 * @example
 * ```tsx
 * const { 
 *   listaEnderecos, 
 *   buscandoEndereco,
 *   buscarSugestoes,
 *   buscarDetalhes,
 *   limparSugestoes
 * } = useGooglePlacesSimple({
 *   onError: (msg) => setTextToast(msg),
 *   onLoading: setShowLoading
 * });
 * 
 * // Buscar sugestões
 * buscarSugestoes("Avenida Paulista");
 * 
 * // Buscar detalhes
 * const detalhes = await buscarDetalhes(placeId);
 * console.log(detalhes.latitude, detalhes.longitude);
 * ```
 */
export const useGooglePlacesSimple = ({
    onError,
    onLoading,
    debounceTime = 2000,
    minChars = 4
}: UseGooglePlacesSimpleProps) => {
    const history = useHistory();

    // Estados
    const [listaEnderecos, setListaEnderecos] = useState<SugestaoEndereco[]>([]);
    const [buscandoEndereco, setBuscandoEndereco] = useState(false);
    const [ultimaBuscaRealizada, setUltimaBuscaRealizada] = useState('');

    // Ref para timeout de debounce
    const searchTimeout = useRef<any>(null);

    /**
     * Limpa a lista de sugestões
     */
    const limparSugestoes = useCallback(() => {
        setListaEnderecos([]);
        setUltimaBuscaRealizada('');
        setBuscandoEndereco(false);
    }, []);

    /**
     * Busca sugestões de endereço com debounce (sem necessidade de empresa)
     */
    const buscarSugestoes = useCallback((enderecoTexto: string, forceBusca: boolean = false) => {
        // Validações iniciais
        if (enderecoTexto.trim() === "") {
            limparSugestoes();
            return;
        }

        if (enderecoTexto.trim().length < minChars) {
            setListaEnderecos([]);
            setBuscandoEndereco(false);
            return;
        }

        // Evitar busca duplicada
        if (!forceBusca && enderecoTexto.trim() === ultimaBuscaRealizada) {
            return;
        }

        // Limpar timeout anterior
        if (searchTimeout.current) {
            clearTimeout(searchTimeout.current);
        }

        // Criar novo timeout com debounce
        searchTimeout.current = setTimeout(() => {
            const enderecoTratado = enderecoTexto.toLowerCase().replace(/\s/g, "%20");
            const urlCompleta = buscarPlacesSimples.url.replace("{endereco}", enderecoTratado);

            setBuscandoEndereco(true);

            requestService(
                urlCompleta,
                {
                    method: buscarPlacesSimples.method,
                    headers: [["token", window.localStorage.getItem("token")]],
                    credentials: 'include'
                },
                history,
                (response: any) => {
                    console.log("Resposta buscarPlacesSimples:", response);

                    let sugestoes: SugestaoEndereco[] = [];

                    // Suporta dois formatos de resposta:
                    // 1. response.data.sugestoes (novo formato aninhado)
                    // 2. response.data (formato antigo direto)
                    const dadosSugestoes = response?.data?.sugestoes || response?.data;

                    if (dadosSugestoes && Array.isArray(dadosSugestoes)) {
                        sugestoes = dadosSugestoes.map((item: any) => ({
                            placeId: item.placeId || item.place_id,
                            descricao: item.descricao || item.description,
                            descricaoPrincipal: item.descricaoPrincipal || item.structured_formatting?.main_text || item.description || item.descricao,
                            descricaoSecundaria: item.descricaoSecundaria || item.structured_formatting?.secondary_text || '',
                            tipos: item.tipos || item.types || []
                        }));
                    }

                    if (sugestoes.length > 0) {
                        console.log("Sugestões processadas:", sugestoes);
                        setListaEnderecos(sugestoes);
                        setUltimaBuscaRealizada(enderecoTexto.trim());
                    } else {
                        console.log("Nenhuma sugestão encontrada");
                        setListaEnderecos([]);
                    }
                },
                (error: any) => {
                    console.log("Erro ao buscar sugestões:", error);
                    onError?.("Erro ao buscar endereços. Tente novamente.");
                }
            ).finally(() => {
                setBuscandoEndereco(false);
            });
        }, forceBusca ? 0 : debounceTime);
    }, [ultimaBuscaRealizada, minChars, debounceTime, history, onError, limparSugestoes]);

    /**
     * Busca detalhes completos do endereço selecionado (sem validação de área)
     */
    const buscarDetalhes = useCallback(
        (placeId: string, enderecoFormatado: string): Promise<DetalhesEnderecoSimples> => {
            return new Promise((resolve, reject) => {
                const urlCompleta = buscarPlaceDetailsSimples.url.replace("{placeid}", placeId);

                onLoading?.(true);

                requestService(
                    urlCompleta,
                    {
                        method: buscarPlaceDetailsSimples.method,
                        headers: [["token", window.localStorage.getItem("token")]],
                        credentials: 'include'
                    },
                    history,
                    (response: any) => {
                        console.log("Resposta completa buscarPlaceDetailsSimples:", response);

                        if (response && response.data) {
                            const responseData = response.data;

                            // Validar dados essenciais
                            if (responseData.sucesso && responseData.latitude && responseData.longitude) {
                                const resultado: DetalhesEnderecoSimples = {
                                    sucesso: responseData.sucesso,
                                    endereco: enderecoFormatado,
                                    nome: responseData.nome,
                                    latitude: responseData.latitude,
                                    longitude: responseData.longitude,
                                    componentes: responseData.componentes || [],
                                    distancia: responseData.distancia
                                };

                                // Limpar sugestões após seleção
                                setListaEnderecos([]);

                                console.log("Detalhes extraídos:", resultado);
                                resolve(resultado);
                            } else {
                                const erro = "Erro ao extrair dados do endereço.";
                                console.error("Dados incompletos na resposta:", responseData);
                                onError?.(erro);
                                reject(new Error(erro));
                            }
                        } else {
                            const erro = "Resposta inválida da API";
                            onError?.(erro);
                            reject(new Error(erro));
                        }
                    },
                    (error: any) => {
                        console.log("Erro ao buscar detalhes:", error);
                        const mensagem = error.message || "Erro ao localizar endereço.";
                        onError?.(mensagem);
                        reject(new Error(mensagem));
                    }
                ).finally(() => {
                    onLoading?.(false);
                });
            });
        },
        [history, onError, onLoading]
    );

    // Cleanup do timeout ao desmontar
    useState(() => {
        return () => {
            if (searchTimeout.current) {
                clearTimeout(searchTimeout.current);
            }
        };
    });

    return {
        listaEnderecos,
        buscandoEndereco,
        buscarSugestoes,
        buscarDetalhes,
        limparSugestoes
    };
};
